import React, { useEffect, useMemo, useReducer } from 'react';

import { UserDataGroupResponse } from '@ariksa/abac/api';
import {
  AccessRequestGetResponse,
  SnowflakePrivilegeOption,
} from '@ariksa/jit/api';
import { Stack, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useDispatch, useSelector } from 'react-redux';

import { selectDataCatalog } from '../selectors';
import { actions } from '../slice';

import {
  accessTypeOptions,
  PermissionOption,
  RequestAccessForm,
  RequestAccessFormState,
} from './RequestAccessForm';

dayjs.extend(utc);

interface RequestAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  dataGroup: UserDataGroupResponse;
  request?: AccessRequestGetResponse;
  getDataGroups: () => void;
}

const defaultDuration = { label: '4 h', value: '4h' };

const toPermissionOption = (
  choice: SnowflakePrivilegeOption,
): PermissionOption => ({
  value: choice.privilege,
  label: (
    <Stack spacing={0}>
      <Text fontWeight="medium" color="black">
        {choice.label}
      </Text>
      <Text fontSize="sm" color="gray.500">
        {choice.description}
      </Text>
    </Stack>
  ),
  chip: `${choice.group}: ${choice.label}`,
});

const groupPermissions = (choices: SnowflakePrivilegeOption[]) => {
  const groups: Record<string, PermissionOption[]> = {};
  choices.forEach(choice => {
    const { group } = choice;
    groups[group] = [...(groups[group] ?? []), toPermissionOption(choice)];
  });
  return Object.entries(groups).map(([label, options]) => ({ label, options }));
};

const getTime = (date: Date) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}`;

const getDuration = (request?: AccessRequestGetResponse) => {
  const durationHours = request?.duration_seconds
    ? Math.round(request.duration_seconds / 60 / 60)
    : request?.requested_start_time && request?.requested_end_time
    ? Math.round(
        (new Date(request.requested_end_time).getTime() -
          new Date(request.requested_start_time).getTime()) /
          (60 * 60 * 1000),
      )
    : 0;

  return durationHours > 0
    ? { label: `${durationHours} h`, value: `${durationHours}h` }
    : defaultDuration;
};

export const RequestAccessModal = (props: RequestAccessModalProps) => {
  const dispatch = useDispatch();
  const { privilegeCatalog } = useSelector(selectDataCatalog);
  const { dataGroup, onClose, request, getDataGroups } = props;
  const currentDate = new Date();
  const requestStartDate = request?.requested_start_time
    ? dayjs.utc(request.requested_start_time).local().toDate()
    : currentDate;
  const requestDetails = request?.access_request_details?.snowflake;
  const requestRole =
    request?.role_name ?? requestDetails?.role_name ?? requestDetails?.role;
  const requestPrivileges: string[] = useMemo(
    () => requestDetails?.custom_access?.privileges ?? [],
    [requestDetails],
  );

  const [state, updateState] = useReducer(
    (prev: RequestAccessFormState, next: Partial<RequestAccessFormState>) => {
      return { ...prev, ...next };
    },
    {
      roleOptions: [],
      role: requestRole
        ? { label: requestRole, value: requestRole }
        : undefined,
      accessType: requestPrivileges.length
        ? accessTypeOptions.custom
        : accessTypeOptions.role,
      permissions: [],
      duration: getDuration(request),
      time: {
        startDate: requestStartDate,
        startTime: getTime(requestStartDate),
      },
      reason: request?.reason,
    },
  );

  useEffect(() => {
    const options = (dataGroup.eligible_access?.roles ?? []).map(role => ({
      label: role.name,
      value: role.name,
      crudLevels: role.crud_levels ?? [],
    }));
    updateState({
      roleOptions: options,
      role: requestRole
        ? options.find(option => option.value === requestRole) ?? {
            label: requestRole,
            value: requestRole,
          }
        : options[0],
    });
  }, [dataGroup, requestRole]);

  useEffect(() => {
    dispatch(
      actions.getPrivilegeCatalog({
        q: {
          accountId: dataGroup.account.account_id,
          dataGroupId: dataGroup.uuid,
        },
      }),
    );
  }, [dispatch, dataGroup]);

  const choices = privilegeCatalog.data?.permissions;
  const permissionGroups = useMemo(() => groupPermissions(choices ?? []), [
    choices,
  ]);
  const customUnavailableReason = privilegeCatalog.data?.unavailable_reason;
  // Custom Role can be picked once JIT has answered that it is available.
  const isCustomAvailable =
    privilegeCatalog.isSuccess && !customUnavailableReason;

  // A saved request shows what it asked for, even values no longer offered.
  const savedPermissions = useMemo(
    () =>
      requestPrivileges.map(value => {
        const choice = choices?.find(option => option.privilege === value);
        return choice
          ? toPermissionOption(choice)
          : { value, label: value, chip: value };
      }),
    [choices, requestPrivileges],
  );

  const handleSubmit = () => {
    const startDate = state.time?.startDate
      ? new Date(state.time.startDate)
      : new Date();
    const [hours = 0, minutes = 0] = String(state.time?.startTime || '00:00')
      .split(':')
      .map(Number);
    startDate.setHours(hours, minutes, 0, 0);

    const duration = state.duration?.value ?? state.duration ?? '4h';
    const durationHours = Number.parseInt(String(duration), 10);
    const endDate = new Date(
      startDate.getTime() + durationHours * 60 * 60 * 1000,
    );

    dispatch(
      actions.createRequest({
        q: {
          cloud_type: dataGroup.account.cloud_provider,
          requested_start_time: startDate.toISOString(),
          requested_end_time: endDate.toISOString(),
          reason: state.reason,
          snowflake: {
            account_id: dataGroup.account.account_id,
            data_group_id: dataGroup.uuid,
            ...(state.accessType?.value === 'custom'
              ? {
                  custom_access: {
                    scope: 'DATA_GROUP',
                    privileges: state.permissions.map(option => option.value),
                  },
                }
              : { role: state.role?.value ?? state.role }),
          },
        },
        onSuccess: () => {
          getDataGroups();
          onClose();
        },
      }),
    );
  };

  return (
    <RequestAccessForm
      {...props}
      isReadOnly={!!request}
      state={request ? { ...state, permissions: savedPermissions } : state}
      updateState={updateState}
      handleSubmit={handleSubmit}
      permissionGroups={permissionGroups}
      customUnavailableReason={customUnavailableReason}
      isCustomAvailable={isCustomAvailable}
    />
  );
};
